/**
 * SidebarBuilderDndContext - DnD context wrapper for the sidebar builder
 *
 * Handles four drag scenarios:
 * 1. Pool → Category (add feature)
 * 2. Item reorder within category
 * 3. Item move between categories
 * 4. Category reorder
 */

import React, { useState, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  pointerWithin,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { GripVertical, Box } from 'lucide-react';
import { MODULE_ICONS } from '../../../constants/profile_constants.js';

function DragOverlayContent({ activeData, allFeatures }) {
  if (!activeData) return null;

  if (activeData.type === 'category') {
    return (
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-blue-500 px-4 py-2 flex items-center gap-2">
        <GripVertical size={16} className="text-blue-500" />
        <span className="text-sm font-semibold text-gray-700 dark:text-slate-300">
          Groupe
        </span>
      </div>
    );
  }

  // Pool item or canvas item
  const featureId = activeData.featureId;
  const feature = allFeatures.find(f => f.id === featureId) || { id: featureId, label: featureId };
  const Icon = MODULE_ICONS[featureId] || Box;

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl border-2 border-blue-500 px-3 py-1.5 flex items-center gap-2">
      <GripVertical size={12} className="text-blue-500" />
      <Icon size={14} className="text-blue-500" />
      <span className="text-sm text-gray-700 dark:text-slate-300">{feature.label}</span>
    </div>
  );
}

export function SidebarBuilderDndContext({ children, state, onStateChange, allFeatures }) {
  const [activeData, setActiveData] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragStart = useCallback((event) => {
    const { active } = event;
    setActiveData(active.data.current);
  }, []);

  const handleDragOver = useCallback(() => {
    // Visual feedback handled by dnd-kit isOver
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveData(null);

    if (!over || !active) return;

    const activeType = active.data.current?.type;
    const overType = over.data.current?.type;
    const activeId = active.id;
    const overId = over.id;

    // SCENARIO 1: Pool item → Category drop zone
    if (activeType === 'pool-item') {
      const featureId = active.data.current.featureId;

      // Find target category from the over element
      let targetCatId = null;
      let targetIndex = null;

      if (overType === 'category-drop') {
        targetCatId = over.data.current.categoryId;
      } else if (overType === 'canvas-item' || String(overId).startsWith('item-')) {
        // Dropped on an item — find which category it belongs to
        const overFeatureId = over.data.current?.featureId || String(overId).replace('item-', '');
        for (const cat of state.categories) {
          const idx = cat.items.indexOf(overFeatureId);
          if (idx >= 0) {
            targetCatId = cat.id;
            targetIndex = idx;
            break;
          }
        }
      } else if (String(overId).startsWith('cat-') || String(overId).startsWith('drop-cat-')) {
        // Dropped on a category header
        targetCatId = String(overId).replace('drop-', '');
      }

      if (!targetCatId) return;

      // Remove from pool and add to target category
      const newState = {
        categories: state.categories.map(c => ({ ...c, items: [...c.items] })),
        unassigned: [...(state.unassigned || [])],
        pool: (state.pool || []).filter(id => id !== featureId),
      };

      const targetCat = newState.categories.find(c => c.id === targetCatId);
      if (targetCat && !targetCat.items.includes(featureId)) {
        if (targetIndex != null) {
          targetCat.items.splice(targetIndex, 0, featureId);
        } else {
          targetCat.items.push(featureId);
        }
      }

      onStateChange(newState);
      return;
    }

    // SCENARIO 4: Category reorder
    if (activeType === 'category') {
      if (activeId === overId) return;

      const oldIndex = state.categories.findIndex(c => c.id === activeId);
      let newIndex = state.categories.findIndex(c => c.id === overId);
      if (newIndex < 0 && String(overId).startsWith('drop-')) {
        newIndex = state.categories.findIndex(c => c.id === String(overId).replace('drop-', ''));
      }
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;

      const newCategories = [...state.categories];
      const [removed] = newCategories.splice(oldIndex, 1);
      newCategories.splice(newIndex, 0, removed);

      onStateChange({ ...state, categories: newCategories });
      return;
    }

    // SCENARIO 2 & 3: Canvas item reorder / move between categories
    if (activeType === 'canvas-item' || String(activeId).startsWith('item-')) {
      const featureId = active.data.current?.featureId || String(activeId).replace('item-', '');

      // Find source category
      let sourceCatId = null;
      let sourceIndex = -1;
      for (const cat of state.categories) {
        const idx = cat.items.indexOf(featureId);
        if (idx >= 0) {
          sourceCatId = cat.id;
          sourceIndex = idx;
          break;
        }
      }
      if (!sourceCatId) return;

      // Find target category and index
      let targetCatId = null;
      let targetIndex = null;

      if (overType === 'category-drop') {
        targetCatId = over.data.current.categoryId;
      } else if (String(overId).startsWith('item-')) {
        const overFeatureId = over.data.current?.featureId || String(overId).replace('item-', '');
        for (const cat of state.categories) {
          const idx = cat.items.indexOf(overFeatureId);
          if (idx >= 0) {
            targetCatId = cat.id;
            targetIndex = idx;
            break;
          }
        }
      } else if (String(overId).startsWith('cat-') || String(overId).startsWith('drop-cat-')) {
        targetCatId = String(overId).replace('drop-', '');
      }

      if (!targetCatId) return;

      const newState = {
        ...state,
        categories: state.categories.map(c => ({ ...c, items: [...c.items] })),
      };

      const sourceCat = newState.categories.find(c => c.id === sourceCatId);
      const targetCat = newState.categories.find(c => c.id === targetCatId);

      if (sourceCatId === targetCatId) {
        // SCENARIO 2: Reorder within same category
        if (targetIndex == null || sourceIndex === targetIndex) return;
        const [removed] = sourceCat.items.splice(sourceIndex, 1);
        sourceCat.items.splice(targetIndex, 0, removed);
      } else {
        // SCENARIO 3: Move between categories
        sourceCat.items.splice(sourceIndex, 1);
        if (targetIndex != null) {
          targetCat.items.splice(targetIndex, 0, featureId);
        } else {
          targetCat.items.push(featureId);
        }
      }

      onStateChange(newState);
    }
  }, [state, onStateChange]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      {children}
      <DragOverlay dropAnimation={null}>
        {activeData && (
          <DragOverlayContent activeData={activeData} allFeatures={allFeatures} />
        )}
      </DragOverlay>
    </DndContext>
  );
}

export default SidebarBuilderDndContext;
